import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Pagination } from "@mui/material";
import {
  Box,
  Paper,
  Typography,
  Button,
  Switch,
  Tooltip,
  Chip,
  Skeleton,
  Divider,
  Collapse,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DownloadIcon from "@mui/icons-material/Download";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CloseIcon from "@mui/icons-material/Close";
import FirmwarePackageUploadDialog from "./FirmwarePackageUploadDialog";
import FirmwarePackageCards from "./FirmwarePackageCards";
import { can } from "../../../auth/can";
import { useAuth } from "../../../auth/useAuth";
import SearchIcon from "@mui/icons-material/Search";

import { APP_API_V1_BASE } from "../../../config/api";
const API_BASE = APP_API_V1_BASE;

// Proxy policy normalization kept commented for now while frontend runs SAS-only mode.
// const normalizeProxyPolicy = (policy) => ({
//   required: Boolean(policy?.required),
//   baseUrlConfigured: Boolean(policy?.base_url_configured),
//   tokenSigningConfigured: Boolean(policy?.token_signing_configured),
//   tokenTtlSeconds: Number(policy?.token_ttl_seconds || 0),
//   proxySasExpirySeconds: Number(policy?.proxy_sas_expiry_seconds || 0),
// });

export const fetchSasToken = async () => {
  const token = localStorage.getItem("authToken");
  const activeOrgId = localStorage.getItem("activeOrgId");
  if (!token) throw new Error("Missing auth token");
  const res = await axios.get(
    `${APP_API_V1_BASE}/firmware/packages/storage/sas-token`,
    { headers: { Authorization: `Bearer ${token}`, "X-Org-ID": activeOrgId } },
  );
  return res.data;
};

// ── Upload Progress Bar ───────────────────────────────────────────────
function UploadProgressOverlay({ progress, fileName }) {
  if (progress === null) return null;
  const isDone = progress >= 100;
  return (
    <Box
      sx={{
        position: "fixed",
        bottom: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 24 },
        left: { xs: 16, sm: "auto" },
        width: { xs: "auto", sm: 340 },
        zIndex: 2000,
        background: "#fff",
        borderRadius: "14px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        p: 2.5,
        border: "1px solid",
        borderColor: isDone ? "success.light" : "primary.light",
        transition: "border-color 0.3s",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={1}
      >
        <Box>
          <Typography
            fontSize={13}
            fontWeight={700}
            color={isDone ? "success.main" : "primary.main"}
          >
            {isDone ? "✓ Upload Complete" : "Uploading Firmware…"}
          </Typography>
          {fileName && (
            <Typography
              fontSize={11}
              color="text.secondary"
              noWrap
              sx={{ maxWidth: 240 }}
            >
              {fileName}
            </Typography>
          )}
        </Box>
        <Typography
          fontSize={13}
          fontWeight={700}
          color={isDone ? "success.main" : "primary.main"}
        >
          {Math.round(progress)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        color={isDone ? "success" : "primary"}
        sx={{
          height: 6,
          borderRadius: 99,
          backgroundColor: isDone ? "#D1FAE5" : "#DBEAFE",
          "& .MuiLinearProgress-bar": {
            borderRadius: 99,
            transition: "transform 0.2s ease",
          },
        }}
      />
      {isDone && (
        <Typography fontSize={11} color="success.main" mt={0.8}>
          Package added successfully. Refreshing list…
        </Typography>
      )}
    </Box>
  );
}

// ── Version Comparison Modal ──────────────────────────────────────────
function VersionCompareDialog({
  open,
  onClose,
  currentPkg,
  allPackages,
  token,
  orgId,
}) {
  const [compareId, setCompareId] = useState("");
  const [compareDetail, setCompareDetail] = useState(null);
  const [currentDetail, setCurrentDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const candidates = allPackages.filter(
    (p) =>
      p.device_type_id === currentPkg?.device_type_id &&
      p.id !== currentPkg?.id,
  );

  const fetchDetail = useCallback(
    async (id) => {
      const res = await axios.get(
        `${APP_API_V1_BASE}/firmware/packages/${id}`,
        { headers: { Authorization: `Bearer ${token}`, "X-Org-ID": orgId } },
      );
      return res.data;
    },
    [token, orgId],
  );

  useEffect(() => {
    if (!open || !currentPkg) return;
    setCompareId("");
    setCompareDetail(null);
    setCurrentDetail(null);
    setError(null);
    fetchDetail(currentPkg.id)
      .then(setCurrentDetail)
      .catch(() => setError("Failed to load package details."));
  }, [open, currentPkg]);

  useEffect(() => {
    if (!compareId) return;
    setLoading(true);
    setError(null);
    fetchDetail(compareId)
      .then((d) => {
        setCompareDetail(d);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load comparison package.");
        setLoading(false);
      });
  }, [compareId]);

  const fields = [
    { label: "Name", key: "name" },
    { label: "Version", key: "version" },
    {
      label: "Device Type",
      fn: (d) => d?.device_type?.name || d?.device_type_id || "—",
    },
    {
      label: "File Size",
      fn: (d) =>
        d?.file?.size_human ||
        (d?.file_size ? `${(d.file_size / 1024).toFixed(1)} KB` : "—"),
    },
    { label: "Status", fn: (d) => (d?.is_active ? "Active" : "Inactive") },
    { label: "SHA256", fn: (d) => d?.file?.checksum || "—", mono: true },
    { label: "Release Notes", key: "release_notes" },
    { label: "Latest", fn: (d) => (d?.is_latest ? "Yes" : "No") },
    {
      label: "Created At",
      fn: (d) =>
        d?.created_at ? new Date(d.created_at).toLocaleString() : "—",
    },
  ];

  const getVal = (d, field) => (field.fn ? field.fn(d) : d?.[field.key] || "—");
  const isDiff = (a, b) => a !== b && a !== "—" && b !== "—";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: { xs: 0, sm: "16px" }, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          py: 2,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CompareArrowsIcon />
            <Typography fontWeight={700} fontSize={16}>
              Version Comparison
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          mb={3}
        >
          <Box
            p={1.5}
            border="2px solid"
            borderColor="primary.main"
            borderRadius="10px"
            bgcolor="#EFF6FF"
            display="flex"
            mt={2}
            alignItems="center"
            gap={1}
          >
            <Typography fontSize={11} fontWeight={700} color="primary.main">
              CURRENT
            </Typography>
            <Typography fontSize={13} fontWeight={700}>
              {currentPkg?.name}
            </Typography>
            <Chip
              label={`v${currentPkg?.version}`}
              size="small"
              color="primary"
              sx={{ fontSize: 11 }}
            />
          </Box>

          <CompareArrowsIcon
            sx={{
              color: "#94A3B8",
              fontSize: 28,
              flexShrink: 0,
              transform: { xs: "rotate(90deg)", sm: "none" },
              alignSelf: "center",
            }}
          />

          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: 13 }}>Compare with version…</InputLabel>
            <Select
              value={compareId}
              label="Compare with version…"
              onChange={(e) => setCompareId(e.target.value)}
              sx={{ borderRadius: "10px", fontSize: 13 }}
            >
              {candidates.length === 0 ? (
                <MenuItem disabled value="">
                  No other versions for this device type
                </MenuItem>
              ) : (
                candidates.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} — v{p.version}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>

        {compareId &&
          (loading ? (
            <Box>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} height={40} sx={{ mb: 0.5 }} />
              ))}
            </Box>
          ) : compareDetail && currentDetail ? (
            isMobile ? (
              <Box display="flex" flexDirection="column" gap={1}>
                {fields.map((field) => {
                  const valA = getVal(currentDetail, field);
                  const valB = getVal(compareDetail, field);
                  const changed = isDiff(valA, valB);
                  return (
                    <Box
                      key={field.label}
                      sx={{
                        border: "1px solid",
                        borderColor: changed ? "#FCD34D" : "#E2E8F0",
                        borderRadius: "10px",
                        p: 1.5,
                        background: changed ? "#FFFBEB" : "#fff",
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.8}
                      >
                        <Typography
                          fontSize={11}
                          fontWeight={700}
                          color="#64748B"
                          textTransform="uppercase"
                          letterSpacing="0.05em"
                        >
                          {field.label}
                        </Typography>
                        {changed ? (
                          <Chip
                            label="Changed"
                            size="small"
                            sx={{
                              fontSize: 10,
                              bgcolor: "#FEF3C7",
                              color: "#92400E",
                              fontWeight: 700,
                              height: 20,
                            }}
                          />
                        ) : (
                          <Typography fontSize={10} color="text.disabled">
                            Same
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box>
                          <Typography
                            fontSize={10}
                            color="#1D4ED8"
                            fontWeight={600}
                            mb={0.2}
                          >
                            {currentDetail.name} (v{currentDetail.version})
                          </Typography>
                          <Typography
                            fontSize={11}
                            fontWeight={changed ? 600 : 400}
                            color={changed ? "#1D4ED8" : "text.primary"}
                            sx={{
                              fontFamily: field.mono ? "monospace" : "inherit",
                              wordBreak: "break-all",
                            }}
                          >
                            {valA}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box>
                          <Typography
                            fontSize={10}
                            color="#7C3AED"
                            fontWeight={600}
                            mb={0.2}
                          >
                            {compareDetail.name} (v{compareDetail.version})
                          </Typography>
                          <Typography
                            fontSize={11}
                            fontWeight={changed ? 600 : 400}
                            color={changed ? "#7C3AED" : "text.primary"}
                            sx={{
                              fontFamily: field.mono ? "monospace" : "inherit",
                              wordBreak: "break-all",
                            }}
                          >
                            {valB}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table
                  size="small"
                  sx={{ "& td, & th": { fontSize: 12, py: 1.2 } }}
                >
                  <TableHead>
                    <TableRow sx={{ background: "#F8FAFC" }}>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#64748B", width: "22%" }}
                      >
                        FIELD
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#1D4ED8" }}>
                        {currentDetail.name} (v{currentDetail.version})
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#7C3AED" }}>
                        {compareDetail.name} (v{compareDetail.version})
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: "#64748B",
                          width: "10%",
                          textAlign: "center",
                        }}
                      >
                        DIFF
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((field) => {
                      const valA = getVal(currentDetail, field);
                      const valB = getVal(compareDetail, field);
                      const changed = isDiff(valA, valB);
                      return (
                        <TableRow
                          key={field.label}
                          sx={{
                            background: changed ? "#FFFBEB" : "transparent",
                            "&:hover": {
                              background: changed ? "#FEF3C7" : "#F8FAFC",
                            },
                          }}
                        >
                          <TableCell sx={{ color: "#64748B", fontWeight: 600 }}>
                            {field.label}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: field.mono ? "monospace" : "inherit",
                              fontSize: field.mono ? 11 : 12,
                              color: changed ? "#1D4ED8" : "text.primary",
                              fontWeight: changed ? 600 : 400,
                              wordBreak: "break-all",
                            }}
                          >
                            {valA}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: field.mono ? "monospace" : "inherit",
                              fontSize: field.mono ? 11 : 12,
                              color: changed ? "#7C3AED" : "text.primary",
                              fontWeight: changed ? 600 : 400,
                              wordBreak: "break-all",
                            }}
                          >
                            {valB}
                          </TableCell>
                          <TableCell align="center">
                            {changed ? (
                              <Chip
                                label="Changed"
                                size="small"
                                sx={{
                                  fontSize: 10,
                                  bgcolor: "#FEF3C7",
                                  color: "#92400E",
                                  fontWeight: 700,
                                  height: 20,
                                }}
                              />
                            ) : (
                              <Typography fontSize={10} color="text.disabled">
                                Same
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )
          ) : null)}

        {!compareId && !error && (
          <Box textAlign="center" py={4}>
            <CompareArrowsIcon sx={{ fontSize: 40, color: "#CBD5E1", mb: 1 }} />
            <Typography fontSize={13} color="text.disabled">
              Select a version above to see the diff
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", borderRadius: "8px" }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Detail row ────────────────────────────────────────────────────────
const DetailRow = ({ label, value, mono }) => (
  <Box display="flex" gap={2} py={0.8}>
    <Typography
      fontSize={12}
      color="text.secondary"
      sx={{ minWidth: 130, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography
      fontSize={12}
      fontWeight={500}
      sx={{
        fontFamily: mono ? "monospace" : "inherit",
        wordBreak: "break-all",
        color: value ? "text.primary" : "text.disabled",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

// ── Mobile card ───────────────────────────────────────────────────────
function PackageMobileCard({
  pkg,
  serialNumber,
  onToggleActive,
  sasToken,
  sasTokenExpiry,
  canUpdate,
  canDownload,
  allPackages,
  token,
  orgId,
}) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  const handleExpand = async () => {
    setOpen((prev) => !prev);
    if (!detail && !open) {
      setLoadingDetail(true);
      try {
        const res = await axios.get(
          `${APP_API_V1_BASE}/firmware/packages/${pkg.id}`,
          { headers: { Authorization: `Bearer ${token}`, "X-Org-ID": orgId } },
        );
        setDetail(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!sasToken) return alert("Download token missing or expired");
    if (sasTokenExpiry && new Date() > new Date(sasTokenExpiry))
      return alert("Download token has expired");
    if (detail?.file?.url)
      window.open(`${detail.file.url}?${sasToken}`, "_blank");
  };

  const sameDeviceTypeCount = allPackages.filter(
    (p) => p.device_type_id === pkg.device_type_id && p.id !== pkg.id,
  ).length;

  return (
    <>
      <Box
        sx={{
          border: "1px solid",
          borderColor: open ? "primary.main" : "#E2E8F0",
          borderRadius: "18px",
          overflow: "hidden",
          transition: "border-color 0.2s",
        }}
      >
        {/* Card header */}
        <Box
          onClick={handleExpand}
          sx={{
            p: 1.5,
            cursor: "pointer",
            // ✅ EXPANDED ROW COLOR — change #EAF2FF to your preferred color
            background: open ? "#EAF2FF" : "#fff",
            transition: "background 0.2s",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box flex={1} mr={1}>
              <Typography fontSize={13} fontWeight={700} noWrap>
                {pkg.name}
              </Typography>
              <Typography fontSize={11} color="text.disabled">
                S.No: {serialNumber}
              </Typography>
              <Typography fontSize={11} color="text.disabled">
                {pkg.created_at
                  ? new Date(pkg.created_at).toLocaleString()
                  : "—"}
              </Typography>
            </Box>
            <Box sx={{ color: open ? "primary.main" : "#94A3B8", mt: 0.3 }}>
              {open ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </Box>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gap={1}
            mt={1}
            flexWrap="wrap"
          >
            <Chip
              label={`v${pkg.version}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: 11, fontWeight: 700 }}
            />
            <Typography fontSize={12} color="text.secondary">
              {pkg.file_size ? `${(pkg.file_size / 1024).toFixed(1)} KB` : "—"}
            </Typography>
            <Box flex={1} />
            {canUpdate ? (
              <Tooltip title="Inactive firmware cannot be deployed">
                <Switch
                  checked={pkg.is_active}
                  size="small"
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleActive(pkg.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Tooltip>
            ) : (
              <Chip
                label={pkg.is_active ? "Active" : "Inactive"}
                size="small"
                color={pkg.is_active ? "success" : "default"}
                variant="outlined"
                sx={{ fontSize: 11 }}
              />
            )}
          </Box>

          <Typography
            fontSize={10}
            color="text.disabled"
            sx={{ fontFamily: "monospace", mt: 0.5 }}
            noWrap
          >
            {pkg.device_type?.name || pkg.device_type_id}
          </Typography>
        </Box>

        {/* Expanded detail panel */}
        <Collapse in={open}>
          <Divider />
          {/* ✅ EXPANDED DETAIL PANEL COLOR — change #EAF2FF to your preferred color */}
          <Box sx={{ px: 2.1, py: 2.2, background: "#EAF2FF" }}>
            {loadingDetail ? (
              <Box>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} height={22} sx={{ mb: 0.5 }} />
                ))}
              </Box>
            ) : detail ? (
              <>
                <DetailRow
                  label="Device Type"
                  value={detail.device_type?.name || detail.device_type_id}
                />
                <DetailRow label="File Size" value={detail.file?.size_human} />
                <DetailRow label="SHA256" value={detail.file?.checksum} mono />
                <DetailRow label="Release Notes" value={detail.release_notes} />
                <DetailRow
                  label="Status"
                  value={detail.is_active ? "Active" : "Inactive"}
                />
                {detail.is_latest && (
                  <DetailRow label="Tag" value="Latest release" />
                )}

                <Box mt={1.5} display="flex" gap={1} flexWrap="wrap">
                  {canDownload && detail?.permissions?.can_download && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon fontSize="small" />}
                      onClick={handleDownload}
                      sx={{
                        textTransform: "none",
                        fontSize: 12,
                        borderRadius: "8px",
                        "&:hover": {
                          background: "#1D4ED8",
                          color: "#fff",
                          borderColor: "#1D4ED8",
                        },
                      }}
                    >
                      Download
                    </Button>
                  )}
                  {sameDeviceTypeCount > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CompareArrowsIcon fontSize="small" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCompareOpen(true);
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: 12,
                        borderRadius: "8px",
                        borderColor: "#7C3AED",
                        color: "#7C3AED",
                        "&:hover": {
                          background: "#7C3AED",
                          color: "#fff",
                          borderColor: "#7C3AED",
                        },
                      }}
                    >
                      Compare
                    </Button>
                  )}
                </Box>
              </>
            ) : null}
          </Box>
        </Collapse>
      </Box>

      <VersionCompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        currentPkg={pkg}
        allPackages={allPackages}
        token={token}
        orgId={orgId}
      />
    </>
  );
}

// ── Desktop accordion row ─────────────────────────────────────────────
function PackageRow({
  pkg,
  serialNumber,
  onToggleActive,
  sasToken,
  sasTokenExpiry,
  canUpdate,
  canDownload,
  allPackages,
  token,
  orgId,
}) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  const handleExpand = async () => {
    setOpen((prev) => !prev);
    if (!detail && !open) {
      setLoadingDetail(true);
      try {
        const res = await axios.get(
          `${APP_API_V1_BASE}/firmware/packages/${pkg.id}`,
          { headers: { Authorization: `Bearer ${token}`, "X-Org-ID": orgId } },
        );
        setDetail(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!sasToken) return alert("Download token missing or expired");
    if (sasTokenExpiry && new Date() > new Date(sasTokenExpiry))
      return alert("Download token has expired");
    if (detail?.file?.url)
      window.open(`${detail.file.url}?${sasToken}`, "_blank");
  };

  const sameDeviceTypeCount = allPackages.filter(
    (p) => p.device_type_id === pkg.device_type_id && p.id !== pkg.id,
  ).length;

  return (
    <>
      <Box
        sx={{
          border: "1px solid",
          borderColor: open ? "primary.main" : "#E2E8F0",
          // borderRadius: "18px",
          overflow: "hidden",
          transition: "border-color 0.2s",
          "&:hover": { borderColor: open ? "primary.main" : "#CBD5E1" },
        }}
      >
        <Box
          onClick={handleExpand}
          sx={{
            display: "grid",
            gridTemplateColumns: "0.7fr 2fr 2fr 1fr 1.5fr 1fr 36px",
            alignItems: "center",
            gap: 2,
            px: 2.5,
            py: 1.5,
            cursor: "pointer",
            // ✅ EXPANDED ROW HEADER COLOR — change #EAF2FF to your preferred color
            background: open ? "#EAF2FF" : "#fff",
            transition: "background 0.2s",
            // ✅ HOVER COLORS — change these too if needed
            "&:hover": { background: open ? "#EAF2FF" : "#F8FAFC" },
          }}
        >
          <Typography
            fontSize={12}
            fontWeight={700}
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            {serialNumber}
          </Typography>
          <Box>
            <Typography fontSize={13} fontWeight={600} noWrap>
              {pkg.name}
            </Typography>
            <Typography fontSize={11} color="text.disabled">
              {pkg.created_at ? new Date(pkg.created_at).toLocaleString() : "—"}
            </Typography>
          </Box>
          <Typography
            fontSize={11}
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
            noWrap
          >
            {pkg.device_type?.name || pkg.device_type_id}
          </Typography>
          <Chip
            label={`v${pkg.version}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: 11, fontWeight: 700, width: "fit-content" }}
          />
          <Typography fontSize={13} color="text.secondary">
            {pkg.file_size ? `${(pkg.file_size / 1024).toFixed(1)} KB` : "—"}
          </Typography>
          {canUpdate ? (
            <Tooltip title="Inactive firmware cannot be deployed">
              <Switch
                checked={pkg.is_active}
                size="small"
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleActive(pkg.id, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          ) : (
            <Chip
              label={pkg.is_active ? "Active" : "Inactive"}
              size="small"
              color={pkg.is_active ? "success" : "default"}
              variant="outlined"
              sx={{ fontSize: 11 }}
            />
          )}
          <Box
            sx={{
              color: open ? "primary.main" : "#94A3B8",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {open ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </Box>
        </Box>

        <Collapse in={open}>
          <Divider />
          {/* ✅ EXPANDED DETAIL PANEL COLOR — change #EAF2FF to your preferred color */}
          <Box sx={{ px: 2.5, py: 2.6, background: "#EAF2FF" }}>
            {loadingDetail ? (
              <Box>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} height={24} sx={{ mb: 0.5 }} />
                ))}
              </Box>
            ) : detail ? (
              <>
                <Box display="flex" gap={4} flexWrap="wrap">
                  <Box flex={1} minWidth={240}>
                    <DetailRow label="Full Name" value={detail.name} />
                    <DetailRow label="Version" value={detail.version} />
                    <DetailRow
                      label="Device Type"
                      value={detail.device_type?.name || detail.device_type_id}
                    />
                    <DetailRow
                      label="File Size"
                      value={detail.file?.size_human}
                    />
                  </Box>
                  <Box flex={1} minWidth={240}>
                    <DetailRow
                      label="SHA256 Checksum"
                      value={detail.file?.checksum}
                      mono
                    />
                    <DetailRow
                      label="Release Notes"
                      value={detail.release_notes}
                    />
                    <DetailRow
                      label="Status"
                      value={detail.is_active ? "Active" : "Inactive"}
                    />
                    {detail.is_latest && (
                      <DetailRow label="Tag" value="Latest release" />
                    )}
                  </Box>
                </Box>
                <Box mt={2} display="flex" gap={1.5} flexWrap="wrap">
                  {canDownload && detail?.permissions?.can_download && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon fontSize="small" />}
                      onClick={handleDownload}
                      sx={{
                        textTransform: "none",
                        fontSize: 12,
                        borderRadius: "8px",
                        "&:hover": {
                          background: "#1D4ED8",
                          color: "#fff",
                          borderColor: "#1D4ED8",
                        },
                      }}
                    >
                      Download
                    </Button>
                  )}
                  {sameDeviceTypeCount > 0 && (
                    <Tooltip title="Compare this version with another firmware version for the same device type">
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<CompareArrowsIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCompareOpen(true);
                        }}
                        sx={{
                          textTransform: "none",
                          fontSize: 12,
                          borderRadius: "8px",
                          borderColor: "#7C3AED",
                          color: "#7C3AED",
                          "&:hover": {
                            background: "#7C3AED",
                            color: "#fff",
                            borderColor: "#7C3AED",
                          },
                        }}
                      >
                        Compare Versions
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </>
            ) : null}
          </Box>
        </Collapse>
      </Box>

      <VersionCompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        currentPkg={pkg}
        allPackages={allPackages}
        token={token}
        orgId={orgId}
      />
    </>
  );
}

// ── Desktop column headers ────────────────────────────────────────────
function ColumnHeaders() {
  const headers = [
    "S.No",
    "Firmware Name",
    "Device Type",
    "Version",
    "Size",
    "Active",
    "",
  ];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "0.7fr 2fr 2fr 1fr 1.5fr 1fr 36px",
        gap: 2,
        px: 2.8,
        py: 1.6,

        background:
          "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

        borderTopLeftRadius: "16px", // ✅ only top rounded
        borderTopRightRadius: "16px",
      }}
    >
      {headers.map((h, i) => (
        <Typography
          key={i}
          fontSize={11}
          fontWeight={700}
          color="#fbfbfcff"
          sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
        >
          {h}
        </Typography>
      ))}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────
// export default function FirmwarePackages() {
//   const [rows, setRows] = useState([]);
//   const [openUpload, setOpenUpload] = useState(false);
//   const [sasToken, setSasToken] = useState(null);
//   const [expiresAt, setExpiresAt] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [uploadProgress, setUploadProgress] = useState(null);
//   const [uploadFileName, setUploadFileName] = useState("");

//   const { permissions } = useAuth();
//   const token = localStorage.getItem("authToken");
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const isPlatformAdmin = can(permissions, "system.manage");

//   const fetchPackages = async () => {
//     setLoading(true);

//     try {
//       const res = await axios.get(`https://iot-backend.trackingcoredata.com/firmware/packages`, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });
//       setRows(res.data);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (!isPlatformAdmin) return;
//     fetchPackages();
//   }, [isPlatformAdmin]);

//   const toggleActive = async (id, value) => {
//     await axios.patch(
//       `https://iot-backend.trackingcoredata.com/firmware/packages/${id}`,
//       { is_active: value },
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
//           "Content-Type": "application/json",
//         },
//       },
//     );
//     fetchPackages();
//   };

//   useEffect(() => {

//     if (!isPlatformAdmin) return;
//     fetchSasToken()
//       .then((data) => {
//         setSasToken(data.sas_token);
//         setExpiresAt(data.expires_at);
//       })
//       .catch(console.error);
//   }, [isPlatformAdmin]);

//   const handleUploadStart = (fileName) => {
//     setUploadFileName(fileName || "firmware.bin");
//     setUploadProgress(0);
//   };
//   const handleUploadProgress = (percent) => {
//     setUploadProgress(percent);
//   };
//   const handleUploadComplete = () => {
//     setUploadProgress(100);
//     setTimeout(() => {
//       setOpenUpload(false);
//       setUploadProgress(null);
//       setUploadFileName("");
//       fetchPackages();
//     }, 2000);
//   };

//   const canUpdate = isPlatformAdmin && can(permissions, "firmware.update");
//   const canDownload = isPlatformAdmin && can(permissions, "firmware.view");

//   const rowProps = {
//     onToggleActive: toggleActive,
//     sasToken,
//     sasTokenExpiry: expiresAt,
//     canUpdate,
//     canDownload,
//     allPackages: rows,
//     token,
//     orgId: activeOrg?.id,
//   };

//   return (
//     <>

//       {!isPlatformAdmin ?
//        (
//         <Paper
//           sx={{ p: { xs: 2, sm: 3 }, mt: { xs: 5, md: 0 }, borderRadius: "16px" }}
//         >
//           <Alert severity="info" sx={{ borderRadius: 2 }}>
//             Firmware packages are managed only by platform administrators.
//           </Alert>
//         </Paper>
//       ) :
//       (
//         <Paper
//           sx={{ p: { xs: 2, sm: 3 }, mt: { xs: 5, md: 0 }, borderRadius: "16px" }}
//         >
//         {/* Header */}
//         <Box
//           sx={{
//             position: "relative",
//             overflow: "hidden",
//             mb: 4,
//             px: { xs: 2, md: 3 },
//             py: { xs: 3, md: 4 },
//             borderRadius: 5,
//             background:
//               "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
//             boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
//           }}
//         >

//           <Typography fontWeight={700} fontSize={18}>
//             Firmware Packages
//           </Typography>
//           {isPlatformAdmin && can(permissions, "firmware.upload") && (
//             <Button
//               variant="contained"
//               startIcon={<AddIcon />}
//               onClick={() => setOpenUpload(true)}
//               sx={{
//                 textTransform: "none",
//                 borderRadius: "10px",
//                 fontWeight: 600,
//                 fontSize: { xs: 12, sm: 14 },
//               }}
//             >
//               {isMobile ? "Add" : "Add Package"}
//             </Button>
//           )}
//         </Box>

//         <Box mb={3}>
//           <FirmwarePackageCards data={rows} loading={loading} />
//         </Box>

//         {!isMobile && <ColumnHeaders />}

//         {/* List */}
//         {loading ? (
//           <Box display="flex" flexDirection="column" gap={1}>
//             {[1, 2, 3, 4, 5].map((i) => (
//               <Skeleton
//                 key={i}
//                 variant="rounded"
//                 height={isMobile ? 90 : 60}
//                 sx={{ borderRadius: "12px" }}
//               />
//             ))}
//           </Box>
//         ) : rows.length === 0 ? (
//           <Box textAlign="center" py={6}>
//             <Typography color="text.disabled" fontSize={14}>
//               No firmware packages found
//             </Typography>
//             {can(permissions, "firmware.upload") && (
//               <Button
//                 variant="contained"
//                 startIcon={<AddIcon />}
//                 onClick={() => setOpenUpload(true)}
//                 fullWidth={{ xs: true, sm: false }}
//                 sx={{
//                   textTransform: "none",
//                   fontWeight: 600,
//                   borderRadius: 2,
//                   background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
//                   color: "#000",
//                   "&:hover": {
//                     background: "linear-gradient(135deg, #FCD34D, #D97706)",
//                   },
//                   "&:disabled": {
//                     background: "rgba(255,255,255,0.2)",
//                     color: "rgba(255,255,255,0.5)",
//                   },
//                 }}
//               >
//                 Add Package
//               </Button>
//             )}
//           </Box>

//           {/* Summary cards */}
//           <Box mt={5} mb={1}>
//             <FirmwarePackageCards data={rows} loading={loading} />
//           </Box>
//         </Box>

//       <Box
//   sx={{
//     borderRadius: 4,
//     overflow: "hidden", // 🔥 important
//     border: "1px solid rgba(0,0,0,0.08)",
//     background: "#fff",
//   }}
// >
//   {/* Header */}
//   {!isMobile && <ColumnHeaders />}

//   {/* List */}
//   {loading ? (
//     <Box display="flex" flexDirection="column">
//       {[1, 2, 3, 4, 5].map((i) => (
//         <Skeleton
//           key={i}
//           variant="rectangular"
//           height={60}
//         />
//       ))}
//     </Box>
//   ) : rows.length === 0 ? (
//     <Box textAlign="center" py={6}>
//       <Typography color="text.disabled" fontSize={14}>
//         No firmware packages found
//       </Typography>
//     </Box>
//   ) : (
//     <Box display="flex" flexDirection="column">
//       {rows
//   .slice((page - 1) * rowsPerPage, page * rowsPerPage)
//   .map((pkg) =>
//         isMobile ? (
//           <PackageMobileCard key={pkg.id} pkg={pkg} {...rowProps} />
//         ) : (
//           <PackageRow
//         ),
//       )}
//     </Box>
//   )}
// </Box>

//         {!loading && rows.length > 0 && (

//           <Box mt={2} display="flex" justifyContent="flex-end">
//             <Typography fontSize={12} color="text.disabled">
//               {rows.length} package{rows.length !== 1 ? "s" : ""}
//             </Typography>
//           </Box>
//         )}
//         </Paper>
//       )}

//       <UploadProgressOverlay
//         progress={uploadProgress}
//         fileName={uploadFileName}
//       />

//       <FirmwarePackageUploadDialog
//         open={openUpload}
//         onClose={() => {
//           setOpenUpload(false);
//           setUploadProgress(null);
//           setUploadFileName("");
//           if (isPlatformAdmin) {
//             fetchPackages();
//           }
//         }}
//         onCloseProgress={() => setUploadProgress(null)}
//         onUploadStart={handleUploadStart}
//         onUploadProgress={handleUploadProgress}
//         onUploadComplete={handleUploadComplete}
//       />
//     </>
//   );
// }

export default function FirmwarePackages() {
  const [searchTerm, setSearchTerm] = useState("");
  const [rows, setRows] = useState([]);

  const filteredRows = rows.filter((pkg) =>
    pkg.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const [openUpload, setOpenUpload] = useState(false);
  const [sasToken, setSasToken] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [sasLoading, setSasLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadFileName, setUploadFileName] = useState("");

  // ✅ FIX: missing states
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  const { permissions, activeOrg } = useAuth(); // ✅ FIX: activeOrg missing earlier
  const token = localStorage.getItem("authToken");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isPlatformAdmin = can(permissions, "system.manage");

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${APP_API_V1_BASE}/firmware/packages`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      });
      setRows(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isPlatformAdmin) return;
    fetchPackages();
  }, [isPlatformAdmin]);

  const toggleActive = async (id, value) => {
    await axios.patch(
      `${APP_API_V1_BASE}/firmware/packages/${id}`,
      { is_active: value },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          "Content-Type": "application/json",
        },
      },
    );
    fetchPackages();
  };

  const refreshDownloadToken = useCallback(async () => {
    if (!isPlatformAdmin) return;
    try {
      setSasLoading(true);
      const data = await fetchSasToken();
      setSasToken(data?.sas_token || null);
      setExpiresAt(data?.expires_at || null);
    } catch (error) {
      console.error(error);
      setSasToken(null);
      setExpiresAt(null);
    } finally {
      setSasLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    refreshDownloadToken();
  }, [refreshDownloadToken]);

  const handleUploadStart = (fileName) => {
    setUploadFileName(fileName || "firmware.bin");
    setUploadProgress(0);
  };

  const handleUploadProgress = (percent) => {
    setUploadProgress(percent);
  };

  const handleUploadComplete = () => {
    setUploadProgress(100);
    setTimeout(() => {
      setOpenUpload(false);
      setUploadProgress(null);
      setUploadFileName("");
      fetchPackages();
    }, 2000);
  };

  const canUpdate = isPlatformAdmin && can(permissions, "firmware.update");
  const canDownload = isPlatformAdmin && can(permissions, "firmware.view");

  const rowProps = {
    onToggleActive: toggleActive,
    sasToken,
    sasTokenExpiry: expiresAt,
    canUpdate,
    canDownload,
    allPackages: rows,
    token,
    orgId: activeOrg?.id,
  };

  return (
    <>
      {!isPlatformAdmin ? (
        <Paper
          sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: "rgba(255,255,255,0.08)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          <Alert severity="info">
            Firmware packages are managed only by platform administrators.
          </Alert>
        </Paper>
      ) : (
        <>
          {/* ✅ Gradient Box (Header + Button + Stats) */}
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 3,
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
              boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
            }}
          >
            {/* Header */}
            <Box
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                fontWeight={700}
                sx={{
                  fontSize: {
                    xs: "28px", // Mobile
                    md: "40px", // Tablet/Desktop
                  },
                  color: "#fff",
                }}
              >
                Firmware Packages
              </Typography>

              {can(permissions, "firmware.upload") && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenUpload(true)}
                  sx={{
                    px: 2.2,

                    py: 1.1,

                    borderRadius: 999,

                    textTransform: "none",

                    fontWeight: 700,

                    color: "#082F49",

                    background: "linear-gradient(135deg, #FDE68A, #F59E0B)",

                    boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",

                  }}
                >
                  {isMobile ? "Add" : "Add Package"}
                </Button>
              )}
            </Box>

            {/* Stats Cards */}
            <FirmwarePackageCards data={rows} loading={loading} />
          </Box>

          {/* ✅ Table Section (separate) */}
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.08)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <Alert
              severity={expiresAt ? "success" : "warning"}
              sx={{ mb: 2, borderRadius: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={refreshDownloadToken}
                  disabled={sasLoading}
                  sx={{ textTransform: "none" }}
                >
                  {sasLoading ? "Refreshing..." : "Refresh Token"}
                </Button>
              }
            >
              {expiresAt
                ? `Download SAS token active until ${new Date(expiresAt).toLocaleString()}.`
                : "Download SAS token is unavailable. Refresh to try again."}
            </Alert>
            {/* 🔍 CENTER SEARCH INPUT */}
            <Box display="flex" justifyContent="right" mb={2}>
              <TextField
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{
                  width: { xs: "100%", sm: "400px", md: "20%" },
                  backgroundColor: "#fff",
                  borderRadius: 1,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm("")}
                        edge="end"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              {!isMobile && <ColumnHeaders />}

              {loading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height={60} />
                  ))}
                </Box>
              ) : rows.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography>No firmware packages found</Typography>
                </Box>
              ) : (
                <Box>
                  {[...filteredRows]
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                    .map((pkg, index) =>
                      isMobile ? (
                        <PackageMobileCard
                          key={pkg.id}
                          pkg={pkg}
                          serialNumber={(page - 1) * rowsPerPage + index + 1}
                          {...rowProps}
                        />
                      ) : (
                        <PackageRow
                          key={pkg.id}
                          pkg={pkg}
                          serialNumber={(page - 1) * rowsPerPage + index + 1}
                          {...rowProps}
                        />
                      ),
                    )}
                </Box>
              )}
            </Box>

            {/* Pagination */}
            {filteredRows.length > rowsPerPage && (
              <Box mt={2} display="flex" justifyContent="center">
                <Pagination
                  count={Math.ceil(filteredRows.length / rowsPerPage)}
                  page={page}
                  onChange={(e, val) => setPage(val)}
                />
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* Upload Progress */}
      <UploadProgressOverlay
        progress={uploadProgress}
        fileName={uploadFileName}
      />

      {/* Upload Dialog */}
      <FirmwarePackageUploadDialog
        open={openUpload}
        onClose={() => setOpenUpload(false)}
        onCloseProgress={() => setUploadProgress(null)}
        onUploadStart={handleUploadStart}
        onUploadProgress={handleUploadProgress}
        onUploadComplete={handleUploadComplete}
      />
    </>
  );
}



